import { isValidTextFile, detectFormatFromFilename } from './filename'
import type { ShareFormat } from '@core/types/share'

/**
 * Validates a file for import. Returns the detected format on success,
 * or an error message on failure.
 */
export function validateFileImport(
  filename: string,
  fileSize: number,
  maxBytes: number = 1024 * 1024,
): { valid: true; format: ShareFormat } | { valid: false; error: string } {
  if (!isValidTextFile(filename)) {
    return { valid: false, error: 'Please upload a .md or .mdc file' }
  }
  if (fileSize > maxBytes) {
    return { valid: false, error: 'File too large. Maximum size is 1MB' }
  }
  return { valid: true, format: detectFormatFromFilename(filename) }
}

/**
 * Extract a filename from a URL string, returning `undefined` when the URL
 * has no meaningful last segment (e.g. purely numeric IDs).
 */
export function extractFilenameFromUrl(urlString: string): string | undefined {
  try {
    const urlObj = new URL(urlString)
    const lastSegment = urlObj.pathname.split('/').pop()
    if (lastSegment && lastSegment.length > 0 && !/^\d+$/.test(lastSegment)) {
      return lastSegment
    }
  } catch {
    // Invalid URL
  }
  return undefined
}

/**
 * Rewrite a github.com blob URL to its raw.githubusercontent.com equivalent
 * so that the content can be fetched directly.
 */
export function rewriteGitHubUrl(url: string): string {
  if (url.includes('github.com') && !url.includes('raw.githubusercontent.com')) {
    return url
      .replace('github.com', 'raw.githubusercontent.com')
      .replace('/blob/', '/')
  }
  return url
}
